import React, { useState, useRef, useEffect, useContext } from "react";
import { Input, Button, Space, Popconfirm, message } from "antd";
import { ExclamationCircleOutlined, SearchOutlined } from "@ant-design/icons";
import GenericTable from "../../../Components/GenericTable";
import editIcon from "../../../Assets/Icons/editTable.svg";
import delIcon from "../../../Assets/Icons/deleteTable.svg";
import addIcon from "../../../Assets/Icons/add-btn.svg";
import { useNavigate } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import { getUserData, deleteUser } from "../../../API/Admin/user";
import { handleLogout } from "../../../Utils/UpdateUsersState";
import { UserContext } from "../../../App";
import "./style.css";

const AllUsers = () => {
  const { userData, setUserData, setUserToken } = useContext(UserContext);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [lastLogin, setLastLogin] = useState("");
  const [messageApi, contextHolder] = message.useMessage();

  const errorNotification = (msg) => {
    messageApi.error(msg);
  };

  const successNotification = (msg) => {
    messageApi.success(msg);
  };

  const highlightText = (text) => {
    if (!searchText) return text;
    const regex = new RegExp(`(${searchText})`, "gi");
    const parts = String(text).split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <span
          key={index}
          style={{
            backgroundColor: "yellow",
            color: "#d32f2f",
            fontWeight: "bold",
          }}
        >
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    setSearchText("");
    confirm();
  };

  const handlleClick = () => {
    navigate("/add-user");
  };

  const viewDetails = (userId) => {
    navigate(`/view-user-details/${userId}`);
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      const res = await deleteUser(id);
      if (Math.floor(res.status / 100) === 2) {
        successNotification("User deleted successfully");
        res = fetchData();
      } else {
        errorNotification("Failed to delete user");
      }
    } catch (err) {
      errorNotification("Error occurred while deleting user");
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getUserData();

      if (Math.floor(response?.status / 100) === 2) {
        const raw = response?.data;
        const arr = Array.isArray(raw) ? raw : raw?.users || [];
        setLastLogin(response?.data?.last_login);
        setUsers(arr);
      } else {
        errorNotification("Unexpected response from server.");
      }
    } catch (err) {
      errorNotification("Failed to fetch users.");
    } finally {
      setLoading(false);
    }
  };

  function formatDate(timestamp) {
    const date = new Date(timestamp);

    const formattedDate = date.toLocaleString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: true,
    });

    return formattedDate;
  }

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "300px",
        }}
      >
        <ClipLoader color={"#123abc"} loading={loading} size={50} />
      </div>
    );
  }

  const userColumns = [
    {
      title: "Full Name",
      dataIndex: "name",
      key: "name",
      render: (text) => highlightText(text),
      ...getColumnSearchProps("name"),
    },
    {
      title: "Email Address",
      dataIndex: "email",
      key: "email",
      render: (text) => highlightText(text),
      ...getColumnSearchProps("email"),
    },
    {
      title: "Last Login",
      dataIndex: "last_login",
      key: "last_login",
      render: (timestamp) =>
        timestamp ? formatDate(timestamp) : "Not updated yet",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <span className={`status-badge ${status.toLowerCase()}`}>{status}</span>
      ),
      filters: [
        { text: "Active", value: "Active" },
        { text: "Inactive", value: "Inactive" },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <div style={{ display: "flex", gap: "13px" }}>
          <a onClick={() => viewDetails(record.id)}>
            <img src={editIcon} alt="Edit" />
          </a>
          <Popconfirm
            title="Are you sure to delete this?"
            icon={<ExclamationCircleOutlined style={{ color: "red" }} />}
            okText="Yes"
            cancelText="Cancel"
            onConfirm={() => handleDelete(record.id)}
          >
            <a>
              <img src={delIcon} alt="Delete" />
            </a>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="all-users">
      {contextHolder}
      <div className="button">
        <Button type="default" onClick={handlleClick} className="add-btn">
          <img src={addIcon} alt="add user" />
          Create New User
        </Button>
      </div>

      <div className="table">
        <GenericTable dataSource={users || []} columns={userColumns} />
      </div>
    </div>
  );
};

export default AllUsers;
